import { queryOptions, useMutation } from '@tanstack/react-query';
import { t } from 'i18next';
import type {
  AcceptInvitationTokenResponse,
  DeletePasskeyData,
  DeletePasskeyResponse,
  DeleteTotpResponse,
  GetMyInvitationsResponse,
  HandleMembershipInvitationData,
  HandleMembershipInvitationResponse,
  MeAuthData,
  ToggleMfaData,
  User,
} from 'sdk';
import {
  acceptInvitationToken,
  createPasskey,
  deletePasskey,
  deleteTotp,
  getMyInvitations,
  getMyMemberships,
  handleMembershipInvitation,
  toggleMfa,
  type UpdateMeData,
  updateMe,
} from 'sdk';
import type { ApiError } from '~/lib/api';
import { getPasskeyRegistrationCredential } from '~/modules/auth/passkey-credentials';
import { toaster } from '~/modules/common/toaster/toaster';
import { getAndSetMe, getAndSetMeAuthData } from '~/modules/me/helpers';
import type { Passkey } from '~/modules/me/types';
import { userQueryKeys } from '~/modules/user/query';
import { useUserStore } from '~/modules/user/user-store';
import { queryClient } from '~/query/query-client';
import type { MutationData } from '~/query/types';

export const meKeys = {
  all: ['me'] as const,
  auth: ['me', 'auth'] as const,
  invites: ['me', 'invites'] as const,
  memberships: ['me', 'memberships'] as const,
  register: {
    passkey: ['me', 'register', 'passkey'] as const,
  },
  update: {
    info: ['me', 'update', 'info'] as const,
    flags: ['me', 'update', 'flags'] as const,
  },
  delete: {
    passkey: ['me', 'delete', 'passkey'] as const,
    totp: ['me', 'delete', 'totp'] as const,
  },
  handleInvitation: ['me', 'handle-invitation'] as const,
  acceptInvitationToken: ['me', 'accept-invitation-token'] as const,
};

export const meQueryOptions = () => queryOptions({ queryKey: meKeys.all, queryFn: getAndSetMe });

export const meAuthQueryOptions = () => queryOptions({ queryKey: meKeys.auth, queryFn: getAndSetMeAuthData });

export const meInvitationsQueryOptions = () =>
  queryOptions({ queryKey: meKeys.invites, queryFn: () => getMyInvitations() });

export const useUpdateSelfMutation = () => {
  return useMutation<User, ApiError, Omit<UpdateMeData['body'], 'role' | 'userFlags'>>({
    mutationKey: meKeys.update.info,
    mutationFn: (body) => updateMe({ body }),
    onSuccess: (updatedUser) => applyUpdatedSelf(updatedUser),
    gcTime: 1000 * 10,
  });
};

export const useToggleMfaMutation = () => {
  return useMutation<User, ApiError, NonNullable<ToggleMfaData['body']>>({
    mutationKey: meKeys.update.info,
    mutationFn: (body) => toggleMfa({ body }),
    onSuccess: (updatedUser, { mfaRequired: isEnabling }) => {
      applyUpdatedSelf(updatedUser);
      if (isEnabling) queryClient.invalidateQueries({ queryKey: meKeys.auth });
      toaster.success(t(`mfa_${updatedUser.mfaRequired ? 'enabled' : 'disabled'}`));
    },
    gcTime: 1000 * 10,
  });
};

export const useUpdateSelfFlagsMutation = () => {
  return useMutation<User, ApiError, Pick<UpdateMeData['body'], 'userFlags'>>({
    mutationKey: meKeys.update.flags,
    mutationFn: (body) => updateMe({ body }),
    onSuccess: (updatedUser) => applyUpdatedSelf(updatedUser),
    gcTime: 1000 * 10,
  });
};

export const useCreatePasskeyMutation = () => {
  return useMutation<Passkey, ApiError, void>({
    mutationKey: meKeys.register.passkey,
    mutationFn: async () => {
      const credentialData = await getPasskeyRegistrationCredential();
      return createPasskey({ body: credentialData });
    },
    onSuccess: (newPasskey) => {
      queryClient.setQueryData<MeAuthData>(meKeys.auth, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          passkeys: [newPasskey, ...oldData.passkeys],
        };
      });
      toaster.success(t('c:success.passkey_added'));
    },
    onError(error) {
      // On cancel throws error NotAllowedError
      console.error('Error during passkey registration:', error);
      toaster.error(t('error:passkey_registration_failed'));
    },
  });
};

export const useDeletePasskeyMutation = () => {
  return useMutation<DeletePasskeyResponse, ApiError, MutationData<DeletePasskeyData>>({
    mutationKey: meKeys.delete.passkey,
    mutationFn: ({ path }) => deletePasskey({ path }),
    onSuccess: (_data, { path: { id } }) => {
      queryClient.setQueryData<MeAuthData>(meKeys.auth, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          passkeys: oldData.passkeys.filter((passkey) => id !== passkey.id),
        };
      });
      toaster.success(t('c:success.passkey_unlinked'));
    },
    onError(error) {
      console.error('Error removing passkey:', error);
      toaster.error(t('error:passkey_unlink_failed'));
    },
  });
};

export const useDeleteTotpMutation = () => {
  return useMutation<DeleteTotpResponse, ApiError, void>({
    mutationKey: meKeys.delete.totp,
    mutationFn: () => deleteTotp(),
    onSuccess: () => {
      toaster.success(t('c:success.totp_removed'));
      queryClient.setQueryData<MeAuthData>(meKeys.auth, (oldData) => {
        if (!oldData) return oldData;
        return { ...oldData, hasTotp: false };
      });
    },
    onError(error) {
      console.error('Error removing totp:', error);
      toaster.error(t('error:totp_remove_failed'));
    },
  });
};

const applyUpdatedSelf = (updatedUser: User) => {
  const { updateUser } = useUserStore.getState();

  queryClient.setQueryData(userQueryKeys.detail.byId(updatedUser.id), updatedUser);
  updateUser(updatedUser);
};

/** Source of truth for the current user's memberships in the frontend. */
export const myMembershipsQueryOptions = () =>
  queryOptions({
    queryKey: meKeys.memberships,
    queryFn: ({ signal }) => getMyMemberships({ signal }),
    staleTime: 0,
  });

/** Once an invitation is answered: refresh memberships so the menu rebuilds, drop the invite from cache, and say so. */
const onInvitationSettled = async (settledEntity: { id: string }, action: 'accept' | 'reject') => {
  await queryClient.invalidateQueries({ queryKey: meKeys.memberships });

  queryClient.setQueryData<GetMyInvitationsResponse>(meKeys.invites, (oldData) => {
    if (!oldData) return oldData;
    return { ...oldData, items: oldData.items.filter((invite) => invite.entity.id !== settledEntity.id) };
  });

  toaster.success(t('c:invitation_settled', { action: action === 'accept' ? 'accepted' : 'rejected' }));
};

/** Answers an invitation listed in-app, by its id. */
export const useHandleInvitationMutation = () =>
  useMutation<HandleMembershipInvitationResponse, ApiError, MutationData<HandleMembershipInvitationData>>({
    mutationKey: meKeys.handleInvitation,
    mutationFn: ({ path }) => handleMembershipInvitation({ path }),
    onSuccess: (settledEntity, { path: { acceptOrReject } }) => onInvitationSettled(settledEntity, acceptOrReject),
  });

/** Accepts the invitation behind the single-use token cookie, as the signed-in account. */
export const useAcceptInvitationTokenMutation = () =>
  useMutation<AcceptInvitationTokenResponse, ApiError, void>({
    mutationKey: meKeys.acceptInvitationToken,
    mutationFn: () => acceptInvitationToken(),
    onSuccess: (settledEntity) => onInvitationSettled(settledEntity, 'accept'),
  });
